import { whatsappNumberOptions } from "../../cobro/utils/collectionReminders";

// Comunicaciones masivas por el número de WhatsApp del operador. Reglas puras de la UI:
// variables, texto predefinido, vista previa y armado del payload. El backend
// (microservice-packages, whatsapp-broadcasts/broadcast-message.ts) es la fuente de verdad:
// arma el texto de cada destinatario y valida. Lo de acá solo anticipa lo que va a pasar,
// así que si cambian las variables o el texto predefinido allá, hay que cambiarlos acá.

export const BROADCAST_TEXT_MAX_LENGTH = 1000;

export const BROADCAST_MODES = {
  ORDEN_DEMORADA: "orden_demorada",
  FREE_TEXT: "free_text",
};

export const BROADCAST_SCOPES = {
  FILTERS: "filters",
  SELECTION: "selection",
};

/** Variables disponibles, con el campo del ítem de la vista previa que las alimenta. */
export const BROADCAST_VARIABLES = [
  { name: "nombre", labelKey: "Nombre del destinatario", sample: (i) => i?.receiverName },
  { name: "seguimiento", labelKey: "N° de seguimiento", sample: (i) => i?.followingNumber },
  { name: "link", labelKey: "Link de seguimiento", sample: (i) => trackingLink(i?.followingNumber) },
  { name: "remitente", labelKey: "Comercio remitente", sample: (i) => i?.senderBusinessName },
  { name: "operador", labelKey: "Operador logístico", sample: (i) => i?.deliveryBusinessName },
  { name: "comuna", labelKey: "Comuna", sample: (i) => i?.municipality },
];

// Default del backend (WHATSAPP_BROADCAST_TRACKING_URL_BASE). Solo para la vista previa.
const TRACKING_URL_BASE = "https://beartrackapp.com/tracking";

// Exportado: el detalle del pedido lo usa para "Copiar resumen".
export function trackingLink(followingNumber) {
  const value = String(followingNumber ?? "").trim();
  return value ? `${TRACKING_URL_BASE}/${encodeURIComponent(value)}` : "";
}

/** Texto de "orden demorada": el de la plantilla de Meta que reemplaza. `*…*` es negrita. */
export const ORDEN_DEMORADA_TEXT = [
  "*Demora con tu entrega*",
  "",
  "Hola {nombre},",
  "Lamentamos informar que {operador} ha sufrido un retraso en su ruta de reparto, por tanto tu pedido {seguimiento} no podrá ser entregado durante la presente jornada.",
  "",
  "{operador} entregará tu pedido mañana por la mañana.",
  "",
  "Lamentamos las molestias ocasionadas.",
  "",
  "Seguir mi pedido: {link}",
].join("\n");

const VARIABLE_PATTERN = /\{([^{}\s]+)\}/g;
const KNOWN = new Set(BROADCAST_VARIABLES.map((v) => v.name));

/** Variables del texto que no existen (p. ej. un typo como `{nombr}`), sin repetir. */
export function unknownVariablesIn(text) {
  const unknown = new Set();
  for (const match of String(text ?? "").matchAll(VARIABLE_PATTERN)) {
    if (!KNOWN.has(match[1])) unknown.add(match[1]);
  }
  return Array.from(unknown);
}

/** Texto base según el modo. */
export function templateTextFor(mode, freeText) {
  return mode === BROADCAST_MODES.ORDEN_DEMORADA ? ORDEN_DEMORADA_TEXT : String(freeText ?? "").trim();
}

/**
 * Vista previa con los datos de un paquete de muestra. Una variable sin dato queda como
 * `{variable}` a la vista: el backend excluye a ese destinatario, no le manda el hueco.
 */
export function renderPreview(template, sampleItem) {
  const byName = new Map(BROADCAST_VARIABLES.map((v) => [v.name, v]));
  return String(template ?? "").replace(VARIABLE_PATTERN, (whole, name) => {
    const variable = byName.get(name);
    const value = variable && sampleItem ? String(variable.sample(sampleItem) ?? "").trim() : "";
    return value || whole;
  });
}

/**
 * Números elegibles para comunicaciones: los no oficiales que no son solo de Mesa de ayuda
 * (el backend rechaza esos con 422). Mismo criterio de conexión que la cobranza: solo los
 * activos y conectados se pueden elegir.
 */
export function broadcastNumberOptions(integrations) {
  return whatsappNumberOptions(integrations, null).filter((o) => o.purpose !== "helpdesk");
}

/**
 * Body de `POST alerts/whatsapp/broadcasts`. `filters.statuses` llega YA expandido a los
 * códigos numéricos del backend.
 */
export function buildBroadcastPayload({ scope, filters, selectedIds, integrationId, mode, text, idempotencyKey }) {
  const base = {
    integrationId,
    mode,
    idempotencyKey,
    ...(mode === BROADCAST_MODES.FREE_TEXT ? { text: String(text ?? "").trim() } : {}),
  };
  if (scope === BROADCAST_SCOPES.SELECTION) {
    return { ...base, packageIds: Array.from(selectedIds ?? []) };
  }
  return {
    ...base,
    statuses: filters.statuses,
    packageType: filters.packageType || undefined,
    senders: filters.senders?.length ? filters.senders : undefined,
    municipalities: filters.municipalities?.length ? filters.municipalities : undefined,
    collectDateFrom: filters.collectDateFrom || undefined,
    deliveryId: filters.deliveryId || undefined,
    updatedAtFrom: filters.updatedAtFrom || undefined,
    updatedAtTo: filters.updatedAtTo || undefined,
  };
}

/**
 * UUID v4 para la clave de idempotencia de un intento de envío. No es un secreto: solo
 * tiene que ser único, así que sin Web Crypto (contexto no seguro, jsdom) alcanza con
 * Math.random.
 */
export function newIdempotencyKey() {
  const webCrypto = typeof window !== "undefined" ? window.crypto : undefined;
  if (typeof webCrypto?.randomUUID === "function") {
    return webCrypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof webCrypto?.getRandomValues === "function") {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Estados en los que una campaña todavía avanza (la UI sigue consultando su progreso). */
export const isBroadcastRunning = (status) => status === "active" || status === "preparing";
